import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';

import { AppMessage } from '@/common/components';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { ReferralLinksFormFields, useEditReferralLinksMutation, useGetAllReferralLinksQuery } from '@/modules/Sites';

import { ReferralLinksEditFormView } from './ReferralLinksEditFormView';
import { referralLinksFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  referralId: string;
  handleSetEditingListMode: (is?: true) => void;
}

export const ReferralLinksEditFormContainer: FC<Props> = memo(({ siteId, referralId, handleSetEditingListMode }) => {
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<ReferralLinksFormFields>();
  const [formErrors, setFormErrors] = useState<{ index: number, field: string, message: string; } | null>(null);

  const {
    data: referralLinksData,
    error: referralLinksError,
    isLoading: isReferralLinksLoading,
    isError: isReferralLinksError,
    refetch: refetchReferralLinks
  } = useGetAllReferralLinksQuery(siteId, referralId)({
    enabled: !!referralId && !!siteId
  });

  const { mutate: editReferralLinksMutation, isLoading: editReferralLinksIsLoading } =
  useEditReferralLinksMutation(siteId, referralId)({
    onSuccess: async () => {
      await refetchReferralLinks();
      handleSetEditingListMode();
      appMessageApi.success('Реферальні посилання успішно оновлено!');
    },
    onError: (err) => {
      const error: Record<string, string> = extractFormErrors(err)as any;
      const [_, index, field] = Object.keys(error)[0].split(',');
      const message = Object.values(error)[0];

      setFormErrors({ field, index: +index, message });
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: ReferralLinksFormFields = {
    links: referralLinksData?.map((referralLink) => ({
      referralId: referralLink.referral.id,
      url: referralLink.url,
      IsManualyDisactivated: !referralLink.isActive,
      isFrequencyLocked: referralLink.isFrequencyLocked,
      appearanceFrequency: referralLink.appearanceFrequency,
      isActive: referralLink.isActive,
      id: referralLink.id,
      isRemoved: false,
      isManualyDisactivated: false,
      isNew: false,
      siteId: referralLink.referral.site.id,
    })) ?? []
  };

  const handleFinish = useCallback((values: ReferralLinksFormFields): void => {
    setFormErrors(null);
    editReferralLinksMutation(values);
  }, []);

  const handleBack = useCallback(() => handleSetEditingListMode(), [handleSetEditingListMode]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <>
      <FallbackedLayout
        isLoading={ isReferralLinksLoading }
        isError={ isReferralLinksError }
        error={ referralLinksError }
      >
        {
          () => (
            <ReferralLinksEditFormView
              form={ form }
              initialValues={ initialValues }
              isFormLoading={ editReferralLinksIsLoading }
              validationSchema={ referralLinksFormValidationSchema }
              formErrors={ formErrors }
              referralId={ referralId }
              siteId={ siteId }
              handleSetEditingListMode={ handleSetEditingListMode }
              handleBack={ handleBack }
              handleFinish={ handleFinish }
              handleFinishFailed={ handleFinishFailed }
            />
          )
        }
      </FallbackedLayout>
    </>
  );
});

